import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, AtSign, MessageSquare, Users, Info, Trash2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/api/notifications';
import { useNotificationStore } from '@/store/notificationStore';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

const TYPE_ICONS = {
  MENTION: AtSign,
  DM: MessageSquare,
  CHANNEL_INVITE: Users,
  SYSTEM: Info,
};

const TYPE_COLORS = {
  MENTION: 'text-yellow-400 bg-yellow-900/30',
  DM: 'text-blue-400 bg-blue-900/30',
  CHANNEL_INVITE: 'text-green-400 bg-green-900/30',
  SYSTEM: 'text-purple-400 bg-purple-900/30',
};

export default function NotificationPanel() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { notifications, unreadCount, setNotifications, setUnreadCount, markRead, markAllRead, removeNotification } =
    useNotificationStore();

  // Fetch notifications on mount & when panel opens
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list({ limit: 30 }),
    staleTime: 30_000,
  });

  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (data) setNotifications(data.data);
  }, [data, setNotifications]);

  useEffect(() => {
    if (countData !== undefined) setUnreadCount(countData);
  }, [countData, setUnreadCount]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markReadMut = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: (_, id) => {
      markRead(id);
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const markAllMut = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      markAllRead();
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => notificationsApi.remove(id),
    onSuccess: (_, id) => {
      removeNotification(id);
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const handleClick = (notif: (typeof notifications)[0]) => {
    if (!notif.readAt) markReadMut.mutate(notif.id);

    // Navigate based on type
    const meta = notif.metadata as Record<string, string> | null;
    if (notif.type === 'MENTION' && meta?.channelId) {
      navigate(`/channel/${meta.channelId}`);
    } else if (notif.type === 'DM' && meta?.senderId) {
      navigate(`/dm/${meta.senderId}`);
    } else if (notif.type === 'CHANNEL_INVITE' && meta?.channelId) {
      navigate(`/channel/${meta.channelId}`);
    }
    setOpen(false);
  };

  return (
    <div ref={panelRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        title="Уведомления"
        className="w-7 h-7 rounded-full bg-surface-800 text-surface-400 hover:bg-surface-700 hover:text-surface-200 flex items-center justify-center transition-colors flex-shrink-0 relative"
      >
        <Bell size={13} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-80 max-h-[70vh] bg-surface-900 border border-surface-700 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-800">
            <span className="text-sm font-semibold text-surface-100">Уведомления</span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllMut.mutate()}
                className="text-xs text-brand-400 hover:text-brand-300 flex items-center gap-1 transition-colors"
              >
                <CheckCheck size={12} />
                Прочитать все
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell size={28} className="mx-auto mb-2 text-surface-700" />
                <p className="text-surface-500 text-sm">Нет уведомлений</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = TYPE_ICONS[notif.type] ?? Info;
                const colorClass = TYPE_COLORS[notif.type] ?? TYPE_COLORS.SYSTEM;
                return (
                  <div
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-surface-800/50 hover:bg-surface-800 ${
                      !notif.readAt ? 'bg-surface-850' : ''
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-xs font-medium truncate ${!notif.readAt ? 'text-surface-100' : 'text-surface-300'}`}>
                          {notif.title}
                        </p>
                        {!notif.readAt && (
                          <span className="w-2 h-2 rounded-full bg-brand-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-surface-500 truncate mt-0.5">{notif.body}</p>
                      <p className="text-[10px] text-surface-600 mt-1">
                        {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true, locale: ru })}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteMut.mutate(notif.id); }}
                      className="w-6 h-6 rounded flex items-center justify-center text-surface-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
